import { Component, OnInit, ChangeDetectionStrategy, input, inject, signal, Signal, WritableSignal, computed, DestroyRef } from '@angular/core';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { OrderControllerService, OrderService, OrderUtilityService, UserService } from './../../../../services';
import { OrderLine, OrderShipment, OrderLineGroup, User } from './../../../../models';
import { OrderType, OrderDetailsViewType, MediaType, LineStatus, ShipmentResetType } from './../../../../models/enums';
import { OrderShipmentSelection } from '../../models';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { EMPTY } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ContentLoaderModule } from '@ngneat/content-loader';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { RouterLinkActive } from '@angular/router';
import { OrderShipmentDetailsGridComponent } from '../order-shipment-details-grid/order-shipment-details-grid.component';
import { OrderShipmentDetailsBoardComponent } from '../order-shipment-details-board/order-shipment-details-board.component';
import { OrderShipmentDetailsTimelineComponent } from '../order-shipment-details-timeline/order-shipment-details-timeline.component';
import { OrderShipmentDetailsHeaderComponent } from '../order-shipment-details-header/order-shipment-details-header.component';
import { ToastrService } from 'ngx-toastr';
import { DownloadMetadataButtonComponent } from '../download-metadata-button/download-metadata-button.component';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { LoadingSpinnerComponent } from '../../../../shared/components/loading-spinner/loading-spinner.component';

// The body the server sends for a failed request. A model binding or validation failure fills in errors, keyed by
// the parameter name, with one or more messages each. Other ASP.NET Core failures fill in detail or title, and a
// SwankResult error from a controller carries its text in message
interface ErrorResponseBody {
  title?: string;
  detail?: string;
  message?: string;
  errors?: Record<string, string[]>;
}

@Component({
  selector: 'insight-order-shipment-details',
  templateUrl: './order-shipment-details.component.html',
  styleUrls: ['./order-shipment-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ContentLoaderModule,
    MatIcon,
    MatButton,
    MatCheckbox,
    MatMenuModule,
    MatTooltipModule,
    RouterLinkActive,
    FormsModule,
    ReactiveFormsModule,
    DownloadMetadataButtonComponent,
    OrderShipmentDetailsHeaderComponent,
    OrderShipmentDetailsGridComponent,
    OrderShipmentDetailsBoardComponent,
    OrderShipmentDetailsTimelineComponent,
    LoadingSpinnerComponent
  ],
  standalone: true
})
export class OrderShipmentDetailsComponent implements OnInit {
  public readonly orderShipmentConfig = input.required<OrderShipmentSelection>();
  public readonly ShipmentResetType = ShipmentResetType;

  public orderShipment: OrderShipment | null = null;
  public filteredOrderShipment: WritableSignal<OrderShipment> = signal<OrderShipment>(null);

  public orderTypeEnum = OrderType;
  public orderDetailViewEnum = OrderDetailsViewType;
  protected readonly isLoadingOrderDetails = signal<boolean>(false);
  protected readonly orderDetailsHasErrorsLoading = signal<boolean>(false); // in case there are any issues loading the order details
  protected readonly orderSelected = signal<boolean>(false); // The parent has a selection to show
  public resetRunning = signal<boolean>(false);

  public cdkOverlayOrigin: CdkOverlayOrigin; // tooltip for the streaming server portal information
  public missingScheduleOrigin: CdkOverlayOrigin; // tooltip with instructions if the schedule is missing
  public orderLinesFormGroup: FormGroup = new FormGroup({
    viewOnlyErrorsCheckbox: new FormControl(false)
  });

  // Injected services
  public orderUtilityService = inject(OrderUtilityService);
  public readonly orderControllerService = inject(OrderControllerService);
  private titleService = inject(Title);
  private readonly userService = inject(UserService);
  private toastr = inject(ToastrService);
  private orderService = inject(OrderService);
  private destroyRef = inject(DestroyRef); // for cleaning up subscriptions

  public user: Signal<User> = toSignal(this.userService.getUser());
  public isEditor = computed(() => { return this.user()?.permissions.insightEditor; });
  public isOps = computed(() => { return this.user()?.permissions.insightOperations; });

  protected readonly orderDetailsNotFound = signal<boolean>(false); // the server answered 404: the order and shipment do not exist in the source system
  protected readonly requestedShipment = signal<OrderShipmentSelection | null>(null); // the selection being loaded, so error messages name it rather than the last loaded order
  protected readonly errorMessages = signal<string[]>([]); // the messages the server gave for a failed request, shown on the UI

  constructor() {
    this.loadDetailsForSelection();
  }

  /**
   * Loads the order details whenever the selected order shipment changes. switchMap aborts the request for the
   * previous selection and takeUntilDestroyed aborts the one in flight when the user leaves the view. Aborting the
   * HTTP request lets the server cancel its SQL queries instead of finishing work that nobody is waiting for.
   */
  private loadDetailsForSelection(): void {
    toObservable(this.orderShipmentConfig)
      .pipe(
        tap((selection: OrderShipmentSelection | null) => this.startLoading(selection)),
        // A null selection goes through switchMap as an empty request so it still aborts the one in flight,
        // otherwise a late response would overwrite the "no results" view
        switchMap((selection: OrderShipmentSelection | null) => selection === null
          ? EMPTY
          : this.orderControllerService
            .getOrderDetails(selection.orderNumber, selection.shipmentNumber, selection.sourceSystem)
            .pipe(
              // Handled inside the request so a failed selection does not end the stream for the selections after it
              catchError((error: HttpErrorResponse) => {
                this.showLoadingError(error);
                return EMPTY;
              })
            )),
        takeUntilDestroyed()
      )
      .subscribe({
        next: (newData: OrderShipment) => this.showLoadedDetails(newData)
      });
  }

  private updateFilteredShipment(): void {
    const onlyShowErrorLines = this.orderLinesFormGroup.get('viewOnlyErrorsCheckbox')?.value;
    // filter the order lines to show everything...or just the error lines
    if (onlyShowErrorLines) {
      const filteredForErrors = this.orderShipment.lines.filter(line => {
        return line.errors && line.errors.length > 0;
      });

      // update line groups
      const filteredLineGroups: OrderLineGroup[] = this.orderShipment.lineGroups.filter(group => {
        // see if any of the lines in this group are in the filtered lines for errors
        const doesGroupHaveAnyLinesWithErrors = group.orderLines.some(lineInGroup => {
          return filteredForErrors.some(filteredLine => filteredLine.lineNumber === lineInGroup.lineNumber);
        });
        return doesGroupHaveAnyLinesWithErrors;
      });

      // sort the lines in each group so they are in order
      filteredLineGroups.forEach(group => {
        group.orderLines = group.orderLines.sort((a, b) => a.lineNumber - b.lineNumber);
      });

      this.filteredOrderShipment.set(
        { ...this.orderShipment, lines: filteredForErrors, lineGroups: filteredLineGroups }
      );

      return;
    }

    // show all lines
    this.filteredOrderShipment.set(this.orderShipment);
  }

  ngOnInit() {
    this.orderLinesFormGroup.get('viewOnlyErrorsCheckbox')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(onlyShowErrorLines => {
        this.updateFilteredShipment();
      });
  }

  public get noLinesToShow(): boolean {
    return this.filteredOrderShipment()?.lines.length === 0;
  }

  public hasHardDriveBurningError(): boolean {
    if (this.orderShipment.orderType !== OrderType.Adebs || this.orderShipment.mediaType !== MediaType.HardDrive) {
      return false;
    }

    return this.orderShipment.lines.some(x => x.status === LineStatus.Burning && x.errors.length > 0);
  }

  public isShipmentCancelled(): boolean {
    return this.orderUtilityService.isShipmentCancelled(this.orderShipment);
  }

  public isAnyCompletedOrder(): boolean {
    return this.orderUtilityService.isAnyCompletedOrder(this.orderShipment);
  }

  public isCompletedCinelinkOrder(): boolean {
    return this.orderUtilityService.isCompletedCinelinkOrder(this.orderShipment);
  }

  public isAdebsOrder(): boolean {
    return this.orderShipment.orderType === OrderType.Adebs;
  }

  public isMediaTypeCVD(): boolean {
    return this.orderShipment.mediaType === MediaType.Cvd;
  }

  public showHardDriveBurningErrorMessage(): boolean {
    return (this.isOps() || this.isEditor()) && this.hasHardDriveBurningError();
  }

  private startLoading(selection: OrderShipmentSelection | null): void {
    // nothing to show in the details view.
    // This is a valid case. If people are doing filtering and searching and no results are found
    // Fulfillment wants the orders listing to update and the orders detail to update while all filtering/searching
    if (selection === null) {
      this.orderSelected.set(false);
      this.orderDetailsNotFound.set(false);
      this.orderDetailsHasErrorsLoading.set(false);
      return;
    }

    // reset value for this so it doesn't appear while loading new data
    this.orderSelected.set(true);
    this.orderDetailsNotFound.set(false);
    this.isLoadingOrderDetails.set(true);
    this.orderDetailsHasErrorsLoading.set(false);
    this.requestedShipment.set(selection);
  }

  private showLoadedDetails(newData: OrderShipment): void {
    this.orderDetailsHasErrorsLoading.set(false);
    this.isLoadingOrderDetails.set(false);
    this.loadNewOrderShipmentDetails(newData);

    // set page title to current order we have selected
    this.titleService.setTitle(`Insight - Order ${this.orderShipment.orderNumber} ${this.orderShipment.customerName}`);
  }

  private showLoadingError(error: HttpErrorResponse): void {
    this.isLoadingOrderDetails.set(false);
    this.errorMessages.set(this.getErrorMessages(error));

    // The order and shipment do not exist in the source system. That is a bad selection rather than a server
    // problem, so show it as not found instead of as an error
    if (error.status === HttpStatusCode.NotFound) {
      // Drop the last loaded order, otherwise its header and grid stay on screen above the not found notice
      this.orderShipment = null;
      this.orderDetailsNotFound.set(true);

      const requested = this.requestedShipment();
      if (requested !== null) {
        this.titleService.setTitle(`Insight - Order ${requested.orderNumber}:${requested.shipmentNumber} not found`);
      }

      return;
    }

    // There was some server problem grabbing the issue. Display the issue on the UI
    this.orderDetailsHasErrorsLoading.set(true);
  }

  /**
   * The server does not always answer with a plain string. A bad order or shipment number fails model binding, and
   * ASP.NET Core then answers with a problem details body that lists every validation message under errors, grouped
   * by parameter name. Each message is returned on its own, prefixed with the parameter it is about.
   */
  private getErrorMessages(error: HttpErrorResponse): string[] {
    const body: unknown = error.error;

    // plain string
    if (typeof body === 'string' && body.length > 0) {
      return [body];
    }

    // error object with error list
    if (body !== null && typeof body === 'object') {
      const problem = body as ErrorResponseBody;
      // Each parameter can carry several messages, so flatten them to one line per message
      const validationMessages = Object.entries(problem.errors ?? {})
        .flatMap(([parameterName, messages]) => messages.map((message) => `${parameterName}: ${message}`));
      if (validationMessages.length > 0) {
        return validationMessages;
      }

      const description = problem.detail ?? problem.title ?? problem.message;
      if (description) {
        return [description];
      }
    }

    return [error.message];
  }

  private loadNewOrderShipmentDetails(shipment: OrderShipment): void {
    this.orderShipment = shipment;
    if (this.orderShipment.cinelinkBoxIds != null) {
      this.orderShipment.cinelinkBoxIds.filter((box, index) => index < 5); //makes sure only first 5 results are displayed as tooltip would be cluttered otherwise
    }

    this.orderShipment = this.normalizedShipmentData(this.orderShipment);
    this.updateFilteredShipment();
  }

  // occasionally orders do not put things in order groups...but are just in lines
  // put them in groups so we can process and normalize the data
  private normalizedShipmentData(startingShipment: OrderShipment): OrderShipment {
    const normalizedData: OrderShipment = startingShipment;
    if (normalizedData.lineGroups === undefined) {
      normalizedData.lineGroups = [];
    }

    if (normalizedData.lineGroups.length === 0) {
      // create a new order group in the case one doesn't exist for a status
      normalizedData.lines.forEach((line: OrderLine) => {
        // create a lineGroups property in case one doesn't exist
        const doesOrderLineGroupObjectExist = normalizedData.lineGroups !== null; // no line group object
        if (!doesOrderLineGroupObjectExist) {
          normalizedData.lineGroups = [];
        }

        const doesOrderGroupExistForLine = normalizedData.lineGroups.filter(group => group.status === line.status).length > 0;

        if (doesOrderGroupExistForLine === false) {
          const newGroup: OrderLineGroup = new OrderLineGroup();
          newGroup.status = line.status;
          newGroup.orderLines = [];
          normalizedData.lineGroups.push(newGroup);
        }

        // add the entry to it's line order group
        const orderGroupToAddTo = normalizedData.lineGroups
          .filter(group => group.status === line.status)[0];
        orderGroupToAddTo.orderLines.push(line);
      });
    }

    // sort each title by line number. This is easier to read for fulfillment when viewing on the board view
    // this also updates so the lines are in order on the order tooltip showing errors
    normalizedData.lineGroups.forEach((group: OrderLineGroup) => {
      group.orderLines = group.orderLines.sort((a, b) => a.lineNumber - b.lineNumber);
    });

    if (normalizedData.lines) {
      normalizedData.lines.sort((a, b) => a.lineNumber - b.lineNumber);
    }

    return normalizedData;
  }

  public canReset(resetType: ShipmentResetType): boolean {
    return (this.isEditor() || this.isOps()) && this.orderShipment.allowedResets?.includes(resetType);
  }

  public resetShipment(resetType: ShipmentResetType): void {
    this.resetRunning.set(true);
    this.orderService.resetShipment(resetType, this.orderShipment.orderNumber, this.orderShipment.shipmentNumber, this.orderShipment.sourceSystem)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.resetRunning.set(false);
          this.orderControllerService.newOrderDetailsAvailableEvent.next();
        },
        error: (error) => {
          this.resetRunning.set(false);
          this.toastr.error(`Failed to reset order shipment: ${error?.error}`, 'Error');
        }
      });
  }

  public allowsAnyShipmentResets(): boolean {
    return (this.isEditor() || this.isOps()) && this.orderShipment.allowedResets?.length > 0;
  }
}

